import { hide, show, addClass, removeClass, getQueryString, getCurrentLanguageId } from '../../common'
import HttpClient from '../../http-client';
import { FilterParamItem } from '../filter-param-items';

export class ColorOption {
    paramId: number;
    name: string;
    listId: string;
    element: HTMLElement;
    children: ColorChildOption[] = [];
    childrenLoaded: boolean;
    private sublevelElement: HTMLElement;
    private pageId: number;
    private isHidden: boolean;
    private _sortValue: number;
    private _isSelected: boolean;

    get sortValue(): number{
        return this._sortValue;
    }

    get isSelected(): boolean{
        return this._isSelected;
    }

    private colorIconSelectedClass = 'color-icon-selected';

    private filterUpdated: () => void;

    public static CreateOptionElement(optionItem: FilterParamItem): HTMLElement{
        const li = document.createElement('li');

        li.setAttribute('style', `background-color: ${optionItem.Color};`);
        li.classList.add('color-value');
        li.setAttribute('data-list-id', optionItem.ListId.toString());
        li.setAttribute('data-sort', optionItem.Sort.toString());
        li.title = optionItem.Name;

        return li;
    }

    constructor(paramId: number, pageId: number, name: string, listId: string, element: HTMLElement, onFilterUpdated: () => void) {
        this.paramId = paramId;
        this.pageId = pageId;
        this.name = name;
        this.listId = listId;
        this.element = element;
        this._sortValue = Number(element.dataset.sort);
        element.addEventListener('click', (evt: Event) => this.handleFirstLevelOptionClick(evt));
        this.filterUpdated = onFilterUpdated;
    }

    public async toggle() {
        if (this._isSelected) {
            this.unselect();
        } else {
            await this.select();
        }
    }

    public async select(selectChildren: boolean = true): Promise<void> {
        // visually select the element
        addClass(this.element, this.colorIconSelectedClass);

        // load subcolors
        if (!this.childrenLoaded) {
            await this.loadSublevel();
        }
        this.displaySublevel(true);

        // check all unselected subcolors
        if (selectChildren) {
            this.children
                .filter(o => !o.isSelected)
                .forEach(o => o.select());
        }

        this._isSelected = true;
    }

    public unselect(): void {
        // visually unselect the element
        removeClass(this.element, this.colorIconSelectedClass);

        this._isSelected = false;

        if (this.childrenLoaded) {
            this.children.forEach(o => o.unselect());
        }

        this.displaySublevel(false);
    }

    public updateItems(items: FilterParamItem[]): void {
        const item = items.find(i => i.ListId.toString() === this.listId);

        if(!item){
            this.hide();
            if(this.childrenLoaded){
                this.children.forEach(ch => ch.hide());
            }
            return;
        } 
        
        this.show();
        if(this.childrenLoaded){
            this.addChildren(item.FilterValues.filter(f => !this.children.find(ch => ch.listId === f.ListId.toString())))
            this.children.forEach(ch => ch.update(item.FilterValues));
        }
    }

    private addChildren(items: FilterParamItem[]) {
        if(items.length === 0)
            return;

        const options = this.createOptions(items);
        this.children = this.children.concat(options).sort((a,b) => a.sortValue - b.sortValue);
        if(this.childrenLoaded){
            this.element.parentElement.removeChild(this.sublevelElement);
            this.sublevelElement = null;
        }
        this.renderChildren();
    }

    public hide(): void{
        hide(this.element);
        this.displaySublevel(false);
        this.isHidden = true;
    }

    public show(): void{
        this.element.style.display = 'inline-block';
        if(this._isSelected){
            this.displaySublevel(true);
        }
        this.isHidden = false;
    }

    private async loadSublevel(): Promise<void> {
        const items = await this.loadChildren();
        if(items && items.length > 0){
            this.children = this.createOptions(items);
            this.renderChildren();
        }
        this.childrenLoaded = true;
    }

    private renderChildren(): void{
        if(!this.children || this.children.length === 0){
            this.displaySublevel(false);
            return;
        } 

        if(!this.sublevelElement){
            this.sublevelElement = this.createSublevelElement(this.name, this.listId);
        }

        this.children.forEach(o => this.sublevelElement.appendChild(o.element));
        this.element.parentElement.appendChild(this.sublevelElement);
    }

    public hasChildren() {
        return this.children && this.children.length > 0;
    }

    public hasSelectedChildren() {
        return this.hasChildren() && this.children.some(ch => ch.isSelected);
    }

    private createOptions(items: FilterParamItem[]): ColorChildOption[]{
        return items.map(item =>
            new ColorChildOption(item, this, () => this.filterUpdated()));
    }

    private async loadChildren(): Promise<FilterParamItem[]>{
        const urlEndpoint = `/filters/getlistitemchildren/${this.paramId}/${this.listId}`;
        const queryString = getQueryString();
        const url = queryString ? `${urlEndpoint}?${queryString}` : urlEndpoint;

        const response = await new HttpClient().fetch(url, {
            headers: {
                'PageId': this.pageId.toString(),
                'Current-Language-Id': getCurrentLanguageId()
            }
        });
        const items: FilterParamItem[] = JSON.parse(await response.json());
        return items;
    }

    private displaySublevel(display: boolean = true): void {
        if (!this.sublevelElement) {
            return;
        }
        
        if (display && this.children.length > 0) {
            show(this.sublevelElement);
        } else {
            hide(this.sublevelElement);
        }
    }

    private createSublevelElement(colorName: string, parentId: string): HTMLElement {
        const element = document.createElement('div');
        element.setAttribute('data-subcolor-id', parentId);
        const header = document.createElement('h5');
        header.innerHTML = colorName;
        element.appendChild(header);
        return element;
    }

    private async handleFirstLevelOptionClick(evt: Event): Promise<void> {
        evt.preventDefault();
        await this.toggle();

        this.filterUpdated();
    }

}

export class ColorChildOption {
    private _isSelected: boolean;
    element: HTMLElement;
    parent: ColorOption;
    private item: FilterParamItem;
    private isHidden: boolean;

    get listId(): string{
        return this.item.ListId.toString();
    }

    get name(): string{
        return this.item.Name;
    } 
    
    get sortValue(): number{
        return this.item.Sort;
    }

    get isSelected(): boolean{
        return this._isSelected;
    }

    private filterUpdated: () => void;

    constructor(item: FilterParamItem, parent: ColorOption, onFilterUpdated: () => void) {
        this.item = item;
        this.parent = parent;
        this.filterUpdated = onFilterUpdated;

        this.element = this.createColorInputElement()
    }

    public select(): void {
        const linkElement = this.element.querySelector('span');
        addClass(linkElement, 'selected-color-fake-link');
        this.element.querySelector('input').checked = true;
        this._isSelected = true;
    }

    public unselect(): void {
        const linkElement = this.element.querySelector('span');
        removeClass(linkElement, 'selected-color-fake-link');
        this.element.querySelector('input').checked = false;
        this._isSelected = false;
    }

    public update(items: FilterParamItem[]): void {
        const item = items.find(x => x.ListId === this.item.ListId);
        item && this.parent.isSelected ? this.show() : this.hide();
    }

    public hide() {
        hide(this.element);
        this.isHidden = true;
    }

    private show(){
        show(this.element);
        this.isHidden = false;
    }

    private createColorInputElement(): HTMLElement {
        const div = document.createElement('div');
        div.classList.add('color-input');

        const label = document.createElement('label');
        label.dataset.listId = this.listId;
        div.appendChild(label);

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.classList.add('color-checkbox-input');
        input.dataset.listId = this.listId;
        input.dataset.parentListId = this.parent.listId;
        input.name = this.name;
        input.checked = this._isSelected;
        input.addEventListener('change', (evt: Event) => this.handleCheckboxChange(evt));
        label.appendChild(input);

        const span = document.createElement('span');
        span.classList.add('color-fake-link');
        if (this._isSelected) {
            span.classList.add('selected-color-fake-link');
        }
        span.dataset.listId = this.listId;
        span.dataset.parentListId = this.parent.listId;
        span.innerText = this.name;
        span.addEventListener('click', (evt: Event) => this.handleSubcolorLinkClick(evt));
        label.appendChild(span);

        return div;
    }

    private unselectSiblings(){
        const siblings = this.parent.children.filter(o => o.listId !== this.listId);
        siblings.forEach(o => o.unselect());
    }

    private handleSubcolorLinkClick(evt: Event): any {
        evt.preventDefault();
        this.unselectSiblings();
        this.select();

        this.filterUpdated();
    }

    private handleCheckboxChange(evt: Event): void {
        const element = evt.target as HTMLInputElement;
        if (element.checked) {
            this.select();
        } else {
            this.unselect();
        }

        this.filterUpdated();
    }
}
